'use client'

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { IconChevronDown, IconMenu2 } from '@tabler/icons-react'
import clsx from 'clsx'

import { NavMenuCategory } from 'modules/products'
import Button from 'shared/ui/Button'
import { useFocusTrap } from 'shared/ui/hooks/useFocusTrap'

import CategoriesNavigationMenu from './CategoriesNavigationMenu'

import styles from './CategoriesNavigationWeb.module.scss'

const CategoriesNavigationWeb = ({
  categories,
  promotionsSlot
}: {
  categories: NavMenuCategory[]
  promotionsSlot?: ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const categoriesNavigationButtonRef = useRef<HTMLButtonElement>(null)
  const categoriesNavigationMenuRef = useRef<HTMLDivElement | null>(null)

  useFocusTrap({
    container: categoriesNavigationMenuRef,
    active: open,
    initialFocusAnchor: categoriesNavigationMenuRef.current ?? null,
    previouslyFocusedElement: categoriesNavigationButtonRef
  })

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, handleClose])

  return (
    <div className={styles.root}>
      <div
        className={clsx(styles.backdrop, { [styles.backdropOpen]: open })}
        {...{ open }}
        onClick={handleClose}
        id='button-backdrop'
      />
      <Button
        className={styles.navigationButton}
        ref={categoriesNavigationButtonRef}
        id='categories-navigation-button'
        fullWidth
        size='large'
        startIcon={<IconMenu2 />}
        endIcon={<IconChevronDown />}
        variant='tertiaryOutline'
        onClick={open ? handleClose : handleOpen}
        aria-expanded={open}
        aria-haspopup='tree'
        aria-controls='categories-navigation-menu'
      >
        Wszystkie kategorie
      </Button>
      <CategoriesNavigationMenu
        ref={categoriesNavigationMenuRef}
        id='categories-navigation-menu'
        open={open}
        categories={categories}
        promotionsSlot={promotionsSlot}
        isLoading={!categories}
        handleClose={handleClose}
        aria-labelledby='categories-navigation-button'
      />
    </div>
  )
}

export default CategoriesNavigationWeb
